import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { IColourway, IProduct } from './ProductDataStore'
import { AddonInputValue } from '@/types'

export interface IBasketItem {
  id: string
  name: string
  price: number
  colourWay: IProduct
  extras: IProduct[]
  extrasData: AddonInputValue[]
  quantity?: number
}

/** Total number of units in the basket (sums each item's quantity, defaulting to 1). */
export const countBasketUnits = (items: IBasketItem[]): number =>
  items.reduce((total, item) => total + (item.quantity ?? 1), 0)

export interface IKibuCareSelection {
  planId: string
}

interface IBasket {
  itemCount: number
  basketOpen: boolean
  basketItems: IBasketItem[]
  kibuCareSelection: IKibuCareSelection | null
  setItemCount: (count: number) => void
  setBasketOpen: (open: boolean) => void
  addItemToBasket: (item: IBasketItem) => void
  removeItemFromBasket: (itemId: string) => void
  updateBasketItem: (itemId: string, updatedItem: Partial<IBasketItem>) => void
  setItemQuantity: (itemId: string, quantity: number) => void
  setKibuCareSelection: (selection: IKibuCareSelection | null) => void
  clearKibuCareSelection: () => void
  clearBasket: () => void
}

const useBasketStore = create<IBasket>()(
  persist(
    (set) => ({
      itemCount: 0,
      basketOpen: false,
      basketItems: [],
      kibuCareSelection: null,
      setItemCount: (itemCount) => set({ itemCount }),
      setBasketOpen: (basketOpen) => set({ basketOpen }),
      addItemToBasket: (item: IBasketItem) => set((state) => {
        const updatedItems = [...state.basketItems, item]
        return {
          basketItems: updatedItems,
          itemCount: countBasketUnits(updatedItems),
        }
      }),
      removeItemFromBasket: (itemId: string) => set((state) => {
        const updatedItems = state.basketItems.filter(item => item.id !== itemId)
        return {
          basketItems: updatedItems,
          itemCount: countBasketUnits(updatedItems),
          kibuCareSelection: updatedItems.length ? state.kibuCareSelection : null,
        }
      }),
      updateBasketItem: (itemId: string, updatedItem: Partial<IBasketItem>) => set((state) => {
        const updatedItems = state.basketItems.map(item =>
          item.id === itemId
            ? { ...item, ...updatedItem }
            : item
        )
        return {
          basketItems: updatedItems,
          itemCount: countBasketUnits(updatedItems),
        }
      }),
      setItemQuantity: (itemId: string, quantity: number) => set((state) => {
        const safeQuantity = Math.max(1, Math.floor(quantity) || 1)
        const updatedItems = state.basketItems.map(item =>
          item.id === itemId
            ? { ...item, quantity: safeQuantity }
            : item
        )
        return {
          basketItems: updatedItems,
          itemCount: countBasketUnits(updatedItems),
        }
      }),
      setKibuCareSelection: (kibuCareSelection) => set({ kibuCareSelection }),
      clearKibuCareSelection: () => set({ kibuCareSelection: null }),
      clearBasket: () => set({ basketItems: [], itemCount: 0, kibuCareSelection: null }),
    }),
    {
      name: 'basket-storage', // unique name for localStorage
    }
  )
)

export default useBasketStore

